import { Box, Button, HStack, Input, VStack } from '@chakra-ui/react'
import React, { useCallback, useEffect, useState } from 'react'

import type { ToastLike } from '@mangovoice/mango-ui'
import { Field, Modal } from '@mangovoice/mango-ui'

import type { UserApi } from '../api/userApi'
import { useUpdateMyProfile } from '../hooks/useMyProfileMutations'
import type { MyProfile, UpdateMyProfilePayload } from '../types/user.types'

export interface EditMyProfileModalProps {
  open: boolean
  onClose: () => void
  api: UserApi
  userId: number
  profile: MyProfile | null
  toast?: ToastLike
  labels?: {
    title?: string
    update?: string
    close?: string
    firstName?: string
    lastName?: string
    username?: string
    email?: string
    errRequiredFirstName?: string
    errRequiredLastName?: string
    successProfile?: string
  }
  /** Called after a successful profile update (e.g. to sync UI/state). */
  onProfileUpdated?: (payload: UpdateMyProfilePayload) => void
}

export const EditMyProfileModal = ({
  open,
  onClose,
  api,
  userId,
  profile,
  toast,
  labels = {},
  onProfileUpdated,
}: EditMyProfileModalProps) => {
  const t = useCallback(
    (
      key: keyof NonNullable<EditMyProfileModalProps['labels']>,
      fallback: string,
    ) => labels[key] ?? fallback,
    [labels],
  )

  const [firstName, setFirstName] = useState('')
  const [lastName, setLastName] = useState('')

  const updateMutation = useUpdateMyProfile({
    api,
    userId,
  })

  useEffect(() => {
    if (!open || !profile) return
    setFirstName(profile.first_name ?? '')
    setLastName(profile.last_name ?? '')
  }, [open, profile])

  const errFirst = !firstName.trim()
    ? t('errRequiredFirstName', 'Please make an entry for the First Name.')
    : ''
  const errLast = !lastName.trim()
    ? t('errRequiredLastName', 'Please make an entry for the Last Name.')
    : ''

  const canSubmit =
    Boolean(profile) && !errFirst && !errLast && !updateMutation.isPending

  const readOnlyFieldProps = {
    py: 2.5,
    px: 3,
    borderRadius: 'lg' as const,
    bg: 'bg.muted',
    color: 'fg.muted',
    fontSize: 'sm',
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!canSubmit || !profile) return
    const payload: UpdateMyProfilePayload = {
      first_name: firstName.trim(),
      last_name: lastName.trim(),
    }
    try {
      await updateMutation.mutateAsync(payload)
      onProfileUpdated?.(payload)
      toast?.create({
        title: t('successProfile', 'Profile successfully updated.'),
        type: 'success',
      })
      onClose()
    } catch {
      toast?.create({
        title: 'Failed to update profile.',
        type: 'error',
      })
    }
  }

  const isPending = updateMutation.isPending

  return (
    <Modal
      open={open}
      onOpenChange={(d) => {
        if (!d.open && isPending) return
        if (!d.open) onClose()
      }}
      title={t('title', 'Edit My Profile')}
      maxWidth="480px"
      closeOnEscape={!isPending}
      closeOnInteractOutside={!isPending}
      footer={
        <>
          <Button
            type="button"
            variant="outline"
            colorPalette="neutral"
            onClick={onClose}
            disabled={isPending}
            borderRadius="lg"
            px={5}
            py={2.5}
          >
            {t('close', 'Close')}
          </Button>
          <Button
            type="submit"
            form="edit-my-profile-form"
            colorPalette="brand"
            loading={isPending}
            disabled={!canSubmit}
            borderRadius="lg"
            px={5}
            py={2.5}
          >
            {t('update', 'Update Profile')}
          </Button>
        </>
      }
    >
      <form id="edit-my-profile-form" onSubmit={handleSubmit}>
        <VStack gap={5} align="stretch">
          <HStack gap={4} align="flex-start">
            <Field.Root required invalid={!!errFirst} flex={1}>
              <Field.Label>{t('firstName', 'First Name')}</Field.Label>
              <Input
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                placeholder={t('firstName', 'First Name')}
                autoComplete="given-name"
                borderRadius="lg"
              />
              {errFirst && <Field.ErrorText>{errFirst}</Field.ErrorText>}
            </Field.Root>
            <Field.Root required invalid={!!errLast} flex={1}>
              <Field.Label>{t('lastName', 'Last Name')}</Field.Label>
              <Input
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                placeholder={t('lastName', 'Last Name')}
                autoComplete="family-name"
                borderRadius="lg"
              />
              {errLast && <Field.ErrorText>{errLast}</Field.ErrorText>}
            </Field.Root>
          </HStack>
          <Field.Root>
            <Field.Label>{t('username', 'Username')}</Field.Label>
            <Box {...readOnlyFieldProps}>
              {profile?.username?.trim() || '—'}
            </Box>
          </Field.Root>
          <Field.Root>
            <Field.Label>{t('email', 'Email')}</Field.Label>
            <Box {...readOnlyFieldProps}>{profile?.email?.trim() || '—'}</Box>
          </Field.Root>
        </VStack>
      </form>
    </Modal>
  )
}
